import { useEffect, useRef } from 'react';
import {
    AgentSearchCachePayload,
    GoogleSource,
    IndexedGoogleSource,
} from '../types/responses/GoogleAiSearchResponse';
import { useApiTokenContext } from '../providers/ApiTokenProvider/ApiTokenProvider';
import { resolveAiSearchOption } from '../utils/aiSearchOptions';

export type { AgentSearchCachePayload, GoogleSource, IndexedGoogleSource };

export const AI_SEARCH_MAX_SUMMARY_SOURCES = 5;
export const AI_SEARCH_CITATION_REGEX = /\[(\s*\d+(?:\s*,\s*\d+)*)\]/g;
const toArray = <T = any>(value: unknown): T[] => (Array.isArray(value) ? value : []);

const getFirstNonEmptyString = (...candidates: unknown[]): string => {
    const candidate = candidates.find((value) => typeof value === 'string' && value.trim());
    return typeof candidate === 'string' ? candidate : '';
};

export const parseCitationNumbers = (value = ''): number[] => {
    return value
        .split(',')
        .map((entry) => Number(entry.trim()))
        .filter((entry) => Number.isInteger(entry) && entry > 0);
};

const getCitationIndexesFromSummary = (summaryText: string): number[] => {
    AI_SEARCH_CITATION_REGEX.lastIndex = 0;
    const matches: number[] = [];
    let match: RegExpExecArray | null;

    while ((match = AI_SEARCH_CITATION_REGEX.exec(summaryText)) !== null) {
        matches.push(...parseCitationNumbers(match[1]));
    }

    return matches;
};

export const getSummaryCitationsMap = (
    sources: IndexedGoogleSource[],
): Map<number, IndexedGoogleSource> => {
    return new Map(sources.map((source) => [source.citationIndex, source]));
};

export const getSummarySourcesForDisplay = (
    summaryText: string,
    sources: IndexedGoogleSource[],
    maxSources = AI_SEARCH_MAX_SUMMARY_SOURCES,
): IndexedGoogleSource[] => {
    const dedupeByLink = (items: IndexedGoogleSource[]): IndexedGoogleSource[] => {
        const seenLinks = new Set<string>();
        return items.filter((source) => {
            const normalizedLink = source.link.trim().toLowerCase();
            if (!normalizedLink || seenLinks.has(normalizedLink)) {
                return false;
            }
            seenLinks.add(normalizedLink);
            return true;
        });
    };

    const citedIndexes = getCitationIndexesFromSummary(summaryText);

    if (!citedIndexes.length) {
        return dedupeByLink(sources).slice(0, maxSources);
    }

    const sourceMap = getSummaryCitationsMap(sources);
    const citedSources = dedupeByLink(
        citedIndexes
            .map((index) => sourceMap.get(index))
            .filter((source): source is IndexedGoogleSource => Boolean(source)),
    );

    if (citedSources.length >= maxSources) {
        return citedSources.slice(0, maxSources);
    }

    const fallbackSources = dedupeByLink(sources).filter((source) => {
        const normalizedLink = source.link.trim().toLowerCase();
        return !citedSources.some((citedSource) => citedSource.link.trim().toLowerCase() === normalizedLink);
    });

    return [...citedSources, ...fallbackSources]
        .slice(0, maxSources);
};

const normalizeGoogleSource = (source: any): GoogleSource | null => {
    const title = String(source?.title ?? '').trim();
    const link = String(source?.link ?? '').trim();
    if (!title || !link) {
        return null;
    }

    return {
        title,
        link,
        snippet: String(source?.snippet ?? ''),
    };
};

const normalizeIndexedSource = (source: any, fallbackIndex: number): IndexedGoogleSource | null => {
    const normalizedSource = normalizeGoogleSource(source);
    if (!normalizedSource) {
        return null;
    }

    const parsedIndex = Number(source?.citationIndex);
    return {
        ...normalizedSource,
        citationIndex: Number.isInteger(parsedIndex) && parsedIndex > 0
            ? parsedIndex
            : fallbackIndex,
    };
};

const normalizeGoogleSources = (sources: any[]): GoogleSource[] => {
    return sources
        .map((source) => normalizeGoogleSource(source))
        .filter((source): source is GoogleSource => Boolean(source));
};

export const normalizeIndexedSources = (
    sources: any[],
    maxSources = Number.POSITIVE_INFINITY,
): IndexedGoogleSource[] => {
    return sources
        .slice(0, maxSources)
        .map((source, index) => normalizeIndexedSource(source, index + 1))
        .filter((source): source is IndexedGoogleSource => Boolean(source));
};

const getSummaryTextFromResponse = (summary: any): string => String(
    summary?.summaryText
    ?? summary?.summaryWithMetadata?.summary
    ?? '',
).trim();

const shouldAppendTopSearchesFromSummary = (summary: any): boolean => {
    const skippedReasons = summary?.summarySkippedReasons;
    const reasons = Array.isArray(skippedReasons)
        ? skippedReasons
        : typeof skippedReasons === 'string'
            ? [skippedReasons]
            : [];

    return reasons.includes('OUT_OF_DOMAIN_QUERY_IGNORED');
};

const getAgentSnippetText = (snippets: any): string => {
    if (!Array.isArray(snippets)) {
        return '';
    }

    return snippets
        .map((snippet: any) => {
            if (typeof snippet === 'string') {
                return snippet;
            }

            return getFirstNonEmptyString(snippet?.snippet, snippet?.snippetText);
        })
        .filter(Boolean)
        .join(' ');
};

const mapAgentSearchResult = (result: any): GoogleSource | null => {
    const document = result?.document ?? {};
    const derivedStructData = document?.derivedStructData ?? {};
    const linkCandidate = getFirstNonEmptyString(
        derivedStructData?.link,
        derivedStructData?.url,
        derivedStructData?.uri,
        result?.link,
        result?.url,
    );

    const titleCandidate = getFirstNonEmptyString(
        derivedStructData?.title,
        result?.title,
        linkCandidate,
    );

    const snippetCandidate = getFirstNonEmptyString(
        getAgentSnippetText(derivedStructData?.snippets),
        derivedStructData?.snippet,
        result?.snippet,
    );

    if (!linkCandidate || !titleCandidate) {
        return null;
    }

    return {
        title: titleCandidate,
        link: linkCandidate,
        snippet: snippetCandidate ?? '',
    };
};

const getAgentSummarySources = (summary: any): IndexedGoogleSource[] => {
    const summaryWithMetadata = summary?.summaryWithMetadata ?? {};
    const references = toArray(summaryWithMetadata?.references);

    const referenceSources = references
        .map((reference: any, index: number) => normalizeIndexedSource({
            title: reference?.title ?? reference?.uri ?? reference?.link ?? `Source ${index + 1}`,
            link: reference?.uri ?? reference?.link ?? '',
            snippet: '',
            citationIndex: index + 1,
        }, index + 1))
        .filter((source): source is IndexedGoogleSource => Boolean(source));

    if (!referenceSources.length) {
        return [];
    }

    const citationMetadata = summaryWithMetadata?.citationMetadata ?? summary?.citationMetadata;
    const citations = toArray(citationMetadata?.citations);

    const citationIndexes = citations.flatMap((citation: any) => {
        const sourceList = toArray(citation?.sources);
        return sourceList
            .map((source: any) => Number(source?.referenceIndex))
            .filter((sourceIndex) => Number.isInteger(sourceIndex) && sourceIndex >= 0)
            .map((sourceIndex) => sourceIndex + 1);
    });

    if (!citationIndexes.length) {
        return referenceSources.slice(0, AI_SEARCH_MAX_SUMMARY_SOURCES);
    }

    const sourceMap = getSummaryCitationsMap(referenceSources);

    return citationIndexes
        .map((citationIndex) => sourceMap.get(citationIndex))
        .filter((source): source is IndexedGoogleSource => Boolean(source))
        .slice(0, AI_SEARCH_MAX_SUMMARY_SOURCES);
};

export const buildAgentSearchPayload = (data: any): AgentSearchCachePayload => {
    const mappedResults = toArray(data?.results)
        .map(mapAgentSearchResult)
        .filter((result: GoogleSource | null): result is GoogleSource => Boolean(result));

    const summary = data?.summary ?? {};
    const summaryText = getSummaryTextFromResponse(summary);
    const appendTopSearches = shouldAppendTopSearchesFromSummary(summary);
    const summarySources = summaryText
        ? getSummarySourcesForDisplay(summaryText, getAgentSummarySources(summary))
        : [];

    return {
        totalResults: Number(data?.totalSize ?? mappedResults.length),
        results: mappedResults,
        summaryText,
        summarySources,
        appendTopSearches,
    };
};

export const parseAgentSearchCachePayload = (
    cachedValue: string,
    requireSummaryText: boolean,
): AgentSearchCachePayload | null => {
    try {
        const parsed = JSON.parse(cachedValue);
        const totalResults = Number(parsed?.totalResults);
        const results = normalizeGoogleSources(toArray(parsed?.results));

        if (!Number.isFinite(totalResults) || totalResults < 0 || results.length === 0) {
            return null;
        }

        const summaryText = typeof parsed?.summaryText === 'string' ? parsed.summaryText.trim() : '';
        if (requireSummaryText && !summaryText) {
            return null;
        }

        return {
            totalResults,
            results,
            summaryText,
            summarySources: normalizeIndexedSources(toArray(parsed?.summarySources)),
            appendTopSearches: Boolean(parsed?.appendTopSearches),
        };
    } catch {
        return null;
    }
};

const useAiSearch = () => {

    const { getTokenString, apiTokenMap } = useApiTokenContext();
    const discoveryApiKey = useRef<string>(null);

    useEffect(() => {
        if (apiTokenMap && Object.keys(apiTokenMap).length > 0) {
            discoveryApiKey.current = getTokenString('vertexSearchKey');
        }
    }, [apiTokenMap]);

    const getSearchResults = async (query: string, offset?: number) => {
        try {

            if (!discoveryApiKey.current || !query) {
                throw Error('Invalid request');
            }

            const routeConfig = resolveAiSearchOption(window.location.pathname);

            const endpoint = routeConfig.dataStoreId
                ? `dataStores/${routeConfig.dataStoreId}`
                : `engines/${routeConfig.engineId}`;

            const requestUrl = `https://discoveryengine.googleapis.com/v1/projects/${routeConfig.projectId}/locations/${routeConfig.location}/collections/${routeConfig.collectionId}/${endpoint}/servingConfigs/${routeConfig.servingConfigId}:searchLite?key=${discoveryApiKey.current}`;

            const servingConfig = `projects/${routeConfig.projectId}/locations/${routeConfig.location}/collections/${routeConfig.collectionId}/${endpoint}/servingConfigs/${routeConfig.servingConfigId}`;

            const requestBody = {
                query: query,
                servingConfig,
                pageSize: 10,
                offset: offset,
                contentSearchSpec: {
                    snippetSpec: {
                        returnSnippet: true,
                    },
                    summarySpec: {
                        summaryResultCount: routeConfig.summaryResultCount,
                        includeCitations: true,
                        ignoreAdversarialQuery: true,
                        ignoreNonSummarySeekingQuery: false,
                        ignoreJailBreakingQuery: true,
                        ignoreLowRelevantContent: true,
                        ...(routeConfig.preamble ? {
                            modelPromptSpec: {
                                preamble: routeConfig.preamble,
                            },
                        } : {}),
                        modelSpec: {
                            version: routeConfig.modelVersion,
                        },
                        languageCode: 'en-US',
                        useSemanticChunks: true,
                    },
                },
                languageCode: 'en-US',
                safeSearch: true,
            };

            const response = await fetch(requestUrl, {
                method: 'POST',
                body: JSON.stringify(requestBody),
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            return response;

        } catch (error) {
            console.error('Error calling discovery api: ', error);
        }
    };

    return { getSearchResults };

};

export default useAiSearch;
